import {
  Assignment,
  Person,
  History,
  Inventory,
  CurrencyExchange,
  Settings,
  Sync,
  AccountBalance,
  Drafts,
  Send,
  Contacts,
  CalendarMonth,
  Logout,
  AccountCircle,
  MoreHoriz,
  Park,
} from "@mui/icons-material";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { syncDatabase } from "../utils/offlineSync";
import { useAuth0 } from "@auth0/auth0-react";
import navStyle from "./TopNav.module.css";

type NavItem = { label: string; to: string; icon: ReactNode };

const NAV_SECTIONS: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { label: "Customers", to: "/customers", icon: <Person /> },
      { label: "Proposals", to: "/proposals", icon: <Assignment /> },
      { label: "Appointments", to: "/appointments", icon: <CalendarMonth /> },
      { label: "Activity", to: "/activity", icon: <History /> },
    ],
  },
  {
    title: "Communication",
    items: [
      { label: "Emails", to: "/emails", icon: <Send /> },
      { label: "Email Templates", to: "/email-templates", icon: <Drafts /> },
    ],
  },
  {
    title: "Catalog",
    items: [
      { label: "Products", to: "/products", icon: <Inventory /> },
      { label: "Taxes", to: "/taxes", icon: <CurrencyExchange /> },
    ],
  },
  {
    title: "Admin",
    items: [
      { label: "Contact Form", to: "/contact-form", icon: <Contacts /> },
      { label: "QuickBooks", to: "/quickbooks", icon: <AccountBalance /> },
      { label: "Settings", to: "/settings", icon: <Settings /> },
    ],
  },
];

// The phone tab bar shows the everyday pages; the rest are under "More".
const TABS = ["/customers", "/proposals", "/appointments", "/activity"];
const TAB_LABELS: Record<string, string> = { "/appointments": "Schedule" };

function TopNav({ pageTitle }: { pageTitle?: string }) {
  const nav = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [moreOpen, setMoreOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (token === null) {
      nav("/login");
    }
  }, [token, nav]);

  if (token === null) {
    return <></>;
  }

  const doSync = () => {
    setSyncing(true);
    syncDatabase(token)
      .then(() => window.location.reload())
      .catch(() => setSyncing(false));
  };

  const signOut = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      await syncDatabase(token);
    }
    localStorage.removeItem("token");
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  };

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + "/");
  const currentTab =
    TABS.find((t) => isActive(t)) ?? (moreOpen ? "more" : "more-inactive");

  const navList = (
    <>
      {NAV_SECTIONS.map((section, i) => (
        <List
          key={i}
          dense
          subheader={
            section.title && (
              <ListSubheader className={navStyle.subheader}>
                {section.title}
              </ListSubheader>
            )
          }
        >
          {section.items.map((item) => (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              selected={isActive(item.to)}
              onClick={() => setMoreOpen(false)}
              className={navStyle.navItem}
            >
              <ListItemIcon className={navStyle.navIcon}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      ))}
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List dense>
        <ListItemButton disabled>
          <ListItemIcon className={navStyle.navIcon}>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primary={localStorage.getItem("username")} />
        </ListItemButton>
        <ListItemButton onClick={signOut}>
          <ListItemIcon className={navStyle.navIcon}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </List>
    </>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar className={navStyle.toolbar}>
          {!isDesktop && (
            <span className={navStyle.logoSmall}>
              <Park fontSize="small" />
            </span>
          )}
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {pageTitle}
          </Typography>
          <Tooltip title="Sync now">
            <span>
              <IconButton
                color="primary"
                aria-label="sync"
                onClick={doSync}
                disabled={syncing}
              >
                {syncing ? <CircularProgress size={22} /> : <Sync />}
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {isDesktop ? (
        <Drawer
          variant="permanent"
          className={navStyle.sidebarRoot}
          PaperProps={{ className: navStyle.sidebar }}
        >
          <Link to="/customers" className={navStyle.brand}>
            <span className={navStyle.logo}>
              <Park fontSize="small" />
            </span>
            <span>Lifetree CRM</span>
          </Link>
          {navList}
        </Drawer>
      ) : (
        <>
          <Paper className={navStyle.bottomNav} elevation={8}>
            <BottomNavigation showLabels value={currentTab}>
              {TABS.map((to) => {
                const item = NAV_SECTIONS[0].items.find((i) => i.to === to)!;
                return (
                  <BottomNavigationAction
                    key={to}
                    value={to}
                    label={TAB_LABELS[to] || item.label}
                    icon={item.icon}
                    component={Link}
                    to={to}
                  />
                );
              })}
              <BottomNavigationAction
                value="more"
                label="More"
                icon={<MoreHoriz />}
                onClick={() => setMoreOpen(true)}
              />
            </BottomNavigation>
          </Paper>
          <Drawer
            anchor="bottom"
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            PaperProps={{ className: navStyle.moreSheet }}
          >
            {navList}
          </Drawer>
        </>
      )}
    </>
  );
}

export default TopNav;
